import React, { useMemo, useState } from 'react';
import {
    AlertTriangle,
    CheckCircle,
    ChevronDown,
    ChevronUp,
    Copy,
    HelpCircle,
    XCircle,
} from 'lucide-react';

interface ExecutionEnvelopeView {
    mode?: 'answer_only' | 'execution_required';
    executed?: boolean;
    /** Step 7: plan-only mode flag (mode remains execution_required, executed=false) */
    planOnly?: boolean;
    jobId?: string | null;
    executionId?: string | null;
    verdict?: 'SUCCESS' | 'FAILED' | 'INCOMPLETE' | 'BLOCKED' | 'DENIED' | 'NOT_APPLICABLE' | 'UNKNOWN';
    artifactPointers?: string[] | Record<string, string | null | undefined>;
    artifacts?: Record<string, string | null | undefined>;
    reasonCode?: string;
    intentName?: string;
    intentClass?: string;
}

interface ExecutionCardProps {
    executionEnvelope: ExecutionEnvelopeView;
    traceId?: string;
    risk?: string;
}

const copyToClipboard = (value: string) => {
    if (!value || typeof window === 'undefined') return;
    if (navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(value);
        return;
    }
    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
};

const ExecutionBadge: React.FC<{ verdict?: ExecutionEnvelopeView['verdict'] }> = ({ verdict }) => {
    const normalized = verdict ?? 'UNKNOWN';
    const styles: Record<string, { className: string; Icon: typeof CheckCircle }> = {
        SUCCESS: {
            className: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10',
            Icon: CheckCircle,
        },
        FAILED: { className: 'text-red-300 border-red-500/40 bg-red-500/10', Icon: XCircle },
        INCOMPLETE: {
            className: 'text-yellow-300 border-yellow-500/40 bg-yellow-500/10',
            Icon: AlertTriangle,
        },
        BLOCKED: {
            className: 'text-orange-300 border-orange-500/40 bg-orange-500/10',
            Icon: AlertTriangle,
        },
        DENIED: {
            className: 'text-orange-300 border-orange-500/40 bg-orange-500/10',
            Icon: AlertTriangle,
        },
        NOT_APPLICABLE: {
            className: 'text-slate-300 border-slate-500/30 bg-slate-500/10',
            Icon: HelpCircle,
        },
        UNKNOWN: {
            className: 'text-slate-300 border-slate-500/30 bg-slate-500/10',
            Icon: HelpCircle,
        },
    };

    const { className, Icon } = styles[normalized] || styles.UNKNOWN;
    return (
        <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full border ${className}`}
        >
            <Icon className="w-3 h-3" />
            {normalized}
        </span>
    );
};

const ExecutionCard: React.FC<ExecutionCardProps> = ({ executionEnvelope, traceId, risk }) => {
    const [expanded, setExpanded] = useState(false);
    const verdict = executionEnvelope.verdict ?? 'UNKNOWN';
    const mode = executionEnvelope.mode ?? 'answer_only';
    const isPlanOnly = executionEnvelope.planOnly === true;
    const executed =
        executionEnvelope.executed === true
            ? 'Executed'
            : executionEnvelope.executed === false
              ? isPlanOnly
                  ? 'Not executed (plan only)'
                  : 'Not executed'
              : 'Execution unknown';

    const artifactEntries = useMemo(() => {
        const entries: { label: string; value: string }[] = [];
        const { artifactPointers, artifacts } = executionEnvelope;

        if (Array.isArray(artifactPointers)) {
            artifactPointers.forEach((value, index) => {
                if (value) {
                    entries.push({ label: `artifact-${index + 1}`, value });
                }
            });
        } else if (artifactPointers && typeof artifactPointers === 'object') {
            Object.entries(artifactPointers).forEach(([label, value]) => {
                if (typeof value === 'string' && value.trim()) {
                    entries.push({ label, value });
                }
            });
        }

        if (artifacts && typeof artifacts === 'object') {
            Object.entries(artifacts).forEach(([label, value]) => {
                if (typeof value === 'string' && value.trim()) {
                    if (!entries.find(entry => entry.value === value)) {
                        entries.push({ label, value });
                    }
                }
            });
        }

        return entries;
    }, [executionEnvelope]);

    return (
        <div className="arc-glass-panel border border-slate-700/40 rounded-xl px-3 py-2 text-xs text-slate-300">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wide text-slate-400">
                        Execution
                    </span>
                    <ExecutionBadge verdict={verdict} />
                    {isPlanOnly && (
                        <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded-full border border-sky-500/40 bg-sky-500/10 text-sky-200">
                            PLAN ONLY
                        </span>
                    )}
                </div>
                <button
                    type="button"
                    onClick={() => setExpanded(prev => !prev)}
                    className="inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-slate-200"
                >
                    {expanded ? 'Hide details' : 'Details'}
                    {expanded ? (
                        <ChevronUp className="w-3 h-3" />
                    ) : (
                        <ChevronDown className="w-3 h-3" />
                    )}
                </button>
            </div>

            <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-slate-300">
                <span className="px-2 py-0.5 rounded-full bg-slate-800/60 border border-slate-700/40">
                    {isPlanOnly
                        ? 'Plan Only'
                        : mode === 'execution_required'
                          ? 'Execution Required'
                          : 'Answer Only'}
                </span>
                <span className="px-2 py-0.5 rounded-full bg-slate-800/60 border border-slate-700/40">
                    {executed}
                </span>
                {executionEnvelope.intentClass && (
                    <span className="px-2 py-0.5 rounded-full bg-slate-800/60 border border-slate-700/40">
                        Intent: {executionEnvelope.intentClass}
                    </span>
                )}
                {executionEnvelope.reasonCode && (
                    <span className="px-2 py-0.5 rounded-full bg-slate-800/60 border border-slate-700/40">
                        Reason: {executionEnvelope.reasonCode}
                    </span>
                )}
            </div>

            {mode === 'execution_required' && (
                <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-slate-400">
                    {traceId && (
                        <button
                            type="button"
                            onClick={() => copyToClipboard(traceId)}
                            className="inline-flex items-center gap-1 text-slate-400 hover:text-slate-200"
                        >
                            Trace: {traceId}
                            <Copy className="w-3 h-3" />
                        </button>
                    )}
                    {risk && (
                        <span className="px-2 py-0.5 rounded-full bg-slate-900/60 border border-slate-700/40">
                            Risk: {risk}
                        </span>
                    )}
                </div>
            )}

            {expanded && (
                <div className="mt-3 border-t border-slate-700/40 pt-3 text-[11px] text-slate-300 space-y-2">
                    <div className="grid grid-cols-1 gap-1">
                        {executionEnvelope.intentName && (
                            <div>
                                <span className="text-slate-400">Intent name:</span>{' '}
                                <span className="text-slate-200">
                                    {executionEnvelope.intentName}
                                </span>
                            </div>
                        )}
                        {executionEnvelope.reasonCode && (
                            <div>
                                <span className="text-slate-400">Reason code:</span>{' '}
                                <span className="text-slate-200">
                                    {executionEnvelope.reasonCode}
                                </span>
                            </div>
                        )}
                        {executionEnvelope.jobId && (
                            <div>
                                <span className="text-slate-400">Job ID:</span>{' '}
                                <span className="text-slate-200">{executionEnvelope.jobId}</span>
                            </div>
                        )}
                        {executionEnvelope.executionId && (
                            <div>
                                <span className="text-slate-400">Execution ID:</span>{' '}
                                <span className="text-slate-200">
                                    {executionEnvelope.executionId}
                                </span>
                            </div>
                        )}
                    </div>

                    <div>
                        <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">
                            Artifacts
                        </div>
                        {artifactEntries.length === 0 ? (
                            <div className="text-slate-500">No artifact pointers provided.</div>
                        ) : (
                            <div className="space-y-1">
                                {artifactEntries.map(item => (
                                    <div
                                        key={`${item.label}-${item.value}`}
                                        className="flex items-center justify-between gap-2 rounded-md border border-slate-700/40 bg-slate-900/40 px-2 py-1"
                                    >
                                        <span className="text-slate-400">{item.label}</span>
                                        <div className="flex items-center gap-2">
                                            <span className="text-slate-200 break-all">
                                                {item.value}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => copyToClipboard(item.value)}
                                                className="text-slate-400 hover:text-slate-200"
                                            >
                                                <Copy className="w-3 h-3" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ExecutionCard;
